export type ThreadID = string | undefined;
export type ThreadStatus = 'READY' | 'TYPING' | 'CITING' | 'FETCHING' | 'ERROR';
export type ThreadMessageAuthor = 'USER' | 'ASSISTANT';

export enum ThreadProcessingStatus {
  ASYNC = 'ASYNC',
  LIVE = 'LIVE',
}

export interface Thread {
  id: ThreadID;
  title: string;
  items: ThreadItem[];
  filters: ThreadFilters;
  processingStatus?: ThreadProcessingStatus;
}

export interface APIThread {
  thread_id: string;
  title: string;
  thread_items: APIThreadItem[];
  filters: APIThreadFilters;
}

export interface ThreadResponse {
  response: MessageChunk[];
  enabledCitation: boolean;
  sources?: ResponseSource[];
}

export interface ThreadItem extends ThreadResponse {
  id?: string;
  disliked: boolean;
  prompt: string;
  queryDate: Date;
  // Whether the request asked for Deep Dive; undefined when nobody said
  deepDive?: boolean;
}

export interface APIThreadItem {
  message_id: string;
  query_ts: string;
  user_input: string;
  llm_response?: string;
  rag_sources: ResponseSource[];
  quantitative_sources?: ResponseSource[];
  citations?: {
    response: MessageChunk[];
  };
  // The approach the request asked for, not what the backend ended up doing:
  // 'auto' is Deep Dive, 'normal' is Quick Flight. Only the thread list sends
  // it; the single-thread endpoint leaves it out.
  mode?: 'auto' | 'normal';
}

export interface Citation {
  source_id: number;
  details: {
    content: string;
    relevance: string;
  }[];
}

export interface MessageChunk {
  text: string;
  citation: Citation[];
}

export interface ThreadFilters {
  countries: string[];
  sources: string[];
  earliestDate?: string;
  latestDate?: string;
}

export interface APIThreadFilters {
  source_category: string[];
  publish_date: { earliest: number; latest: number };
  countries: string[];
}
export interface ResponseSource {
  title: string;
  url?: string;
  logo: string;
  provider?: string;
  s3_url?: {
    presigned_url: string;
  };
  columns?: Array<string>;
  sample?: Array<Record<string, unknown>>;
  row_count?: number | string;
}

export interface WSParameters {
  user_sub: string;
  user_input: string;
  deep_dive?: boolean;
  action: 'createThreadPre' | 'addMessageThreadPre';
  thread_id?: string;
  filters?: {
    source_category: string[];
    publish_date: { earliest: number; latest: number };
    countries: string[];
  };
}

export interface WSThread {
  success?: boolean;
  message?: string;
  stream_id?: string;
  data: WSThreadData;
}

export interface WSThreadData {
  thread_id: string;
  user_input: string;
  queryTS?: number;
  connectionId?: string;
  title?: string;
  message_id: string;
  citation?: string;
  type?:
    | 'streamNewMessage'
    | 'streamProcess'
    | 'streamSources'
    | 'streamCitation'
    | 'streamComplete'
    | 'streamError';
  additional_data?: {
    buffered_streaming_contents: string;
  };
  message: string;
  process?: string;
  rag_sources?: ResponseSource[];
  quantitative_sources?: ResponseSource[];
}

export interface ProcessingAction {
  threadID: string;
  type: 'CITATION' | 'QUERY';
  item: Omit<ThreadItem, 'response'>;
  ts: number;
}

export const DEFAULT_FILTERS: ThreadFilters = {
  countries: [],
  sources: [],
};

export interface ThreadsPayload {
  success: boolean;
  message: string;
  data: {
    threadData: APIThread[];
    paginationParams: {
      totalPages: number;
      totalThreadCount: number;
    };
  };
}
export interface PromptApproach {
  label: string;
  id: string;
  timeEstimate: string;
  new?: boolean;
  emoji: string;
  description: string;
}

export const PROMPT_APPROACHES: Array<PromptApproach> = [
  {
    label: 'Quick Flight',
    id: 'quick-flight',
    emoji: '✈️',
    timeEstimate: '10 to 60 seconds.',
    description:
      'An energetic and efficient agent that delivers helpful insights and summaries in no time. Enjoy fast responses powered by the latest data.',
  },
  {
    label: 'Deep Dive',
    id: 'deep-dive',
    emoji: '🤿',
    timeEstimate: '1 to 9 minutes.',
    description:
      'A thoughtful and thorough agent that explores topics in depth. Its answers draw on recent open quantitative data from humanitarian sources alongside qualitative reports.',
  },
];

export const QUICK_FLIGHT_INDEX = PROMPT_APPROACHES.findIndex(
  ({ id }) => id === 'quick-flight'
);
export const DEEP_DIVE_INDEX = PROMPT_APPROACHES.findIndex(
  ({ id }) => id === 'deep-dive'
);

// Router state that asks the prompt box to start on an approach
export interface ApproachRouteState {
  approach: PromptApproach['id'];
}
